import initializeReferralTab from '../referralTab';

const STORAGE_PREFIX = 'referral-coupon-handoff-v1:';
const VALID_STATES = new Set(['pending', 'attempted', 'settled', 'skipped']);
const memoryRecords = new Map();
const unavailableTokens = new Set();

function storageKey(token) {
  return `${STORAGE_PREFIX}${encodeURIComponent(token)}`;
}

function validRecord(record, token) {
  return (
    record?.version === 1 &&
    record.token === token &&
    typeof record.visitId === 'string' &&
    /^[0-9a-f-]{36}$/i.test(record.visitId) &&
    typeof record.discountCode === 'string' &&
    Boolean(record.discountCode.trim()) &&
    VALID_STATES.has(record.status)
  );
}

function writeRecord(record) {
  const { token } = record;
  memoryRecords.set(token, record);
  try {
    window.sessionStorage.setItem(storageKey(token), JSON.stringify(record));
    unavailableTokens.delete(token);
  } catch {
    unavailableTokens.add(token);
  }
}

/** Read only the record belonging to this purchase token. */
export function readReferralCouponHandoff(token) {
  if (typeof token !== 'string' || !token) return null;
  if (unavailableTokens.has(token)) return memoryRecords.get(token) || null;

  let stored;
  try {
    initializeReferralTab();
    stored = window.sessionStorage.getItem(storageKey(token));
  } catch {
    return memoryRecords.get(token) || null;
  }

  if (!stored) {
    memoryRecords.delete(token);
    return null;
  }

  try {
    const record = JSON.parse(stored);
    if (validRecord(record, token)) {
      memoryRecords.set(token, record);
      return record;
    }
  } catch {
    // Invalid tab storage must not revive an earlier in-memory code.
  }

  memoryRecords.delete(token);
  try {
    window.sessionStorage.removeItem(storageKey(token));
  } catch {
    unavailableTokens.add(token);
  }
  return null;
}

/** Preserve the first decision for a token, including a prior attempt or skip. */
export function savePendingReferralCoupon(token, context) {
  if (
    typeof token !== 'string' ||
    !token ||
    !context?.confirmed ||
    typeof context.visitId !== 'string' ||
    !/^[0-9a-f-]{36}$/i.test(context.visitId) ||
    typeof context.discountCode !== 'string' ||
    !context.discountCode.trim() ||
    readReferralCouponHandoff(token)
  )
    return false;

  writeRecord({
    version: 1,
    token,
    visitId: context.visitId,
    discountCode: context.discountCode,
    status: 'pending',
  });
  return true;
}

/** Claim before dispatching the discount request, so remounts cannot retry it. */
export function claimReferralCoupon(token) {
  const record = readReferralCouponHandoff(token);
  if (record?.status !== 'pending') return null;
  writeRecord({ ...record, status: 'attempted' });
  return record.discountCode;
}

/** A purchase GET confirmed the server total after the one coupon attempt. */
export function settleReferralCoupon(token) {
  const record = readReferralCouponHandoff(token);
  if (record?.status !== 'attempted') return false;
  writeRecord({ ...record, status: 'settled' });
  return true;
}

/** Resolve a pending code that checkout cannot apply. */
export function skipReferralCoupon(token) {
  const record = readReferralCouponHandoff(token);
  if (record?.status !== 'pending') return false;
  writeRecord({ ...record, status: 'skipped' });
  return true;
}
